import { MouseEvent as ReactMouseEvent, TouchEvent as ReactTouchEvent, useEffect, useRef } from "react";
import { LuChevronsLeftRight } from "react-icons/lu";
import { TranslationKey } from "../../../shared/i18n/i18n";
import { AdminNavContext, AdminNavList, AdminNavSelection } from "./AdminNavList";

type AdminLeftPaneProps = {
  contextOverride?: AdminNavContext;
  isCollapsed: boolean;
  isDesktopHidden: boolean;
  isMobileNavOpen: boolean;
  isMobileViewport: boolean;
  onCloseMobileNav: () => void;
  onContextChange: (context: AdminNavContext) => void;
  onSelectEntity: (selection: AdminNavSelection | null) => void;
  onStartResize: (event: ReactMouseEvent<HTMLButtonElement> | ReactTouchEvent<HTMLButtonElement>) => void;
  onToggleCollapse: () => void;
  reloadKey: number;
  selectedEntity: AdminNavSelection | null;
  t: (key: TranslationKey) => string;
  width: number;
};

export const AdminLeftPane = ({
  contextOverride,
  isCollapsed,
  isDesktopHidden,
  isMobileNavOpen,
  isMobileViewport,
  onCloseMobileNav,
  onContextChange,
  onSelectEntity,
  onStartResize,
  onToggleCollapse,
  reloadKey,
  selectedEntity,
  t,
  width,
}: AdminLeftPaneProps) => {
  const isPaneCollapsed = !isMobileViewport && isCollapsed;
  const isPaneHidden = !isMobileViewport && isDesktopHidden;
  const paneRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const paneElement = paneRef.current;
    if (!paneElement) {
      return;
    }

    if (isPaneCollapsed || isPaneHidden || isMobileViewport) {
      paneElement.style.removeProperty("--admin-left-pane-width");
      return;
    }

    paneElement.style.setProperty("--admin-left-pane-width", `${width}px`);
  }, [isPaneCollapsed, isPaneHidden, isMobileViewport, width]);

  return (
    <>
      {isMobileViewport ? (
        <button
          aria-hidden={!isMobileNavOpen}
          className={`admin-mobile-nav-backdrop ${isMobileNavOpen ? "is-visible" : ""}`}
          onClick={onCloseMobileNav}
          tabIndex={isMobileNavOpen ? 0 : -1}
          type="button"
        />
      ) : null}
      <aside
        className={[
          "admin-shell-left-pane",
          isPaneHidden ? "is-hidden" : "",
          isPaneCollapsed ? "is-collapsed" : "",
          isMobileViewport ? "is-mobile-drawer" : "",
          isMobileNavOpen ? "is-mobile-open" : "",
        ].filter(Boolean).join(" ")}
        id={isMobileViewport ? "admin-mobile-nav" : undefined}
        ref={paneRef}
      >
        {!isPaneCollapsed ? (
          <AdminNavList
            contextOverride={contextOverride}
            isMobileViewport={isMobileViewport}
            onContextChange={onContextChange}
            onSelectEntity={onSelectEntity}
            reloadKey={reloadKey}
            selectedEntity={selectedEntity}
          />
        ) : null}
      </aside>
      {!isPaneCollapsed && !isPaneHidden && !isMobileViewport ? (
        <button
          aria-label={t("admin.navlist.resizePanel")}
          className="admin-shell-resizer"
          onMouseDown={onStartResize}
          onTouchStart={onStartResize}
          type="button"
        >
          <span aria-hidden="true" className="admin-shell-resizer-handle">
            <LuChevronsLeftRight />
          </span>
        </button>
      ) : null}
    </>
  );
};
